import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateProfile } from '../store/slices/authSlice';
import { addToast } from '../store/slices/toastsSlice';

export const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, loading } = useAppSelector(state => state.auth);

  const [name, setName] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (user) {
      // Use the name if it's different from email, otherwise start empty
      setName(user.name !== user.email ? user.name : '');
    }
  }, [user]);

  const handleNameChange = (value: string) => {
    setName(value);
    if (validationError) {
      setValidationError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setValidationError('Name is required');
      return;
    }

    try {
      await dispatch(updateProfile({ name: name.trim() })).unwrap();
      dispatch(addToast('Profile updated successfully', 'success'));
    } catch (error: any) {
      dispatch(addToast(error.message || 'Failed to update profile', 'error'));
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.heading}>Profile</h1>
        <p style={styles.description}>
          View and update your profile information
        </p>
      </div>

      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Your Information</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={user?.email || ''}
              style={{ ...styles.input, ...styles.inputReadOnly }}
              disabled
            />
            <div style={styles.hint}>Email cannot be changed</div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Display Name</label>
            <input
              type="text"
              value={name}
              onChange={e => handleNameChange(e.target.value)}
              style={{
                ...styles.input,
                ...(validationError ? styles.inputError : {})
              }}
              placeholder="Enter your name"
              disabled={loading}
            />
            {validationError && (
              <div style={styles.error}>{validationError}</div>
            )}
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '2rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '2rem',
  },
  heading: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
    color: '#333',
  },
  description: {
    fontSize: '1rem',
    color: '#666',
    margin: 0,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    marginTop: 0,
    marginBottom: '1.5rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
  },
  inputReadOnly: {
    backgroundColor: '#f5f5f5',
    color: '#666',
    cursor: 'not-allowed',
  },
  inputError: {
    borderColor: '#dc3545',
  },
  hint: {
    color: '#999',
    fontSize: '12px',
    marginTop: '4px',
  },
  error: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '6px',
  },
  button: {
    padding: '12px 24px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    transition: 'background-color 0.2s ease',
  },
  buttonDisabled: {
    backgroundColor: '#90caf9',
    cursor: 'not-allowed',
  },
};
